import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  FileText, 
  Search, 
  Edit, 
  Eye,
  ArrowLeft,
  Filter,
  Download,
  Printer,
  AlertTriangle,
  MessageSquare,
  Phone,
  Check
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useNavigate } from 'react-router-dom';
import ManagementFormDetailPage from './ManagementFormDetailPage';
import axios from 'axios';
import { generateManagementFormPDF } from '../utils/generateManagementFormPDF';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const ManagementFormsLibraryPage = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFormId, setSelectedFormId] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { practice } = useAuth();
  
  // SMS state
  const [patients, setPatients] = useState([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsForm, setSmsForm] = useState(null);
  const [smsPatientSearchQuery, setSmsPatientSearchQuery] = useState('');
  const [filteredSmsPatients, setFilteredSmsPatients] = useState([]);
  const [smsPatient, setSmsPatient] = useState(null);
  const [sendingSMS, setSendingSMS] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Get unique categories from forms
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    loadForms();
    loadPatients();
  }, []);

  // Filter patients for SMS modal - only show patients with phone numbers
  useEffect(() => {
    let filtered = [...patients];
    filtered = filtered.filter(patient => patient.cellPhone || patient.phone);

    if (smsPatientSearchQuery.trim()) {
      const searchLower = smsPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredSmsPatients(filtered);
  }, [smsPatientSearchQuery, patients]);

  const loadPatients = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${backendUrl}/api/practice/patients`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPatients(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load patients:', error);
    }
  };

  const loadForms = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/management-forms`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const formsList = response.data.data || [];
      setForms(formsList);
      
      // Extract unique categories
      const uniqueCategories = ['All', ...new Set(formsList.map(f => f.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to load management forms:', error);
      toast({
        title: "Error",
        description: "Failed to load management forms",
        variant: "destructive"
      });
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredForms = forms.filter(form => {
    const matchesSearch = form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         form.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || form.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleViewForm = (formId) => {
    setSelectedFormId(formId);
  };

  const handleDownloadForm = async (form) => {
    try {
      toast({
        title: "Generating PDF...",
        description: "Please wait...",
        variant: "default",
      });
      
      // Fetch practice data if needed
      let practiceData = practice;
      if (!practice || !practice.name) {
        try {
          const practiceApi = (await import('../services/authApi')).practiceApi;
          const dashboardData = await practiceApi.getDashboard();
          if (dashboardData.success && dashboardData.data?.practice) {
            practiceData = dashboardData.data.practice;
          }
        } catch (error) {
          console.error('Failed to fetch practice data:', error);
        }
      }
      
      const success = await generateManagementFormPDF(form, practiceData);
      
      if (success) {
        toast({
          title: "Downloaded",
          description: "Form downloaded successfully",
          variant: "default",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download form",
        variant: "destructive",
      });
    }
  };

  const handlePrintForm = (form) => {
    const printWindow = window.open('', '', 'height=800,width=800');
    const practiceName = practice?.name || 'Dental Practice';
    
    // Format address properly
    let practiceAddress = '';
    if (practice?.address) {
      if (typeof practice.address === 'object') {
        const addr = practice.address;
        practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      } else {
        practiceAddress = practice.address;
      }
    } else if (practice?.location) {
      practiceAddress = practice.location;
    }
    
    const practicePhone = practice?.phone || '';
    const practiceEmail = practice?.email || '';
    const practiceLogo = practice?.branding?.logo || '';
    
    printWindow.document.write('<html><head><title>' + form.title + '</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('@page { margin: 0.75in; }');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; margin: 0; }');
    printWindow.document.write('.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }');
    printWindow.document.write('.logo { max-width: 150px; max-height: 80px; margin: 0 auto 15px; display: block; }');
    printWindow.document.write('.practice-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }');
    printWindow.document.write('.practice-info { font-size: 12px; color: #666; }');
    printWindow.document.write('.form-title { font-size: 20px; font-weight: bold; margin: 20px 0 10px 0; }');
    printWindow.document.write('.checkbox { display: inline-block; width: 15px; height: 15px; border: 1px solid #333; margin: 0 8px; vertical-align: middle; }');
    printWindow.document.write('pre { white-space: pre-wrap; font-family: Arial; margin-bottom: 60px; page-break-inside: avoid; }');
    printWindow.document.write('.footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666; page-break-inside: avoid; }');
    printWindow.document.write('.footer-title { font-weight: bold; font-size: 12px; margin-bottom: 5px; }');
    printWindow.document.write('@media print { body { padding: 0; } @page { margin: 0.75in; } .footer { position: running(footer); } }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    
    // Header with logo and practice info
    printWindow.document.write('<div class="header">');
    if (practiceLogo) {
      const logoSrc = practiceLogo.startsWith('data:image') ? practiceLogo : `data:image/png;base64,${practiceLogo}`;
      printWindow.document.write('<img src="' + logoSrc + '" class="logo" alt="Practice Logo" />');
    }
    printWindow.document.write('<div class="practice-name">' + practiceName + '</div>');
    if (practiceAddress) printWindow.document.write('<div class="practice-info">' + practiceAddress + '</div>');
    if (practicePhone) printWindow.document.write('<div class="practice-info">Phone: ' + practicePhone + '</div>');
    if (practiceEmail) printWindow.document.write('<div class="practice-info">Email: ' + practiceEmail + '</div>');
    printWindow.document.write('</div>');
    
    // Form title (NO category)
    printWindow.document.write('<h1>' + form.title + '</h1>');
    
    // Replace placeholders and process checkboxes
    let content = form.overview || '';
    content = content.replace(/{PracticeName}/g, practiceName);
    content = content.replace(/{PracticeAddress}/g, practiceAddress);
    content = content.replace(/{PracticePhone}/g, practicePhone);
    content = content.replace(/{PracticeEmail}/g, practiceEmail);
    
    const lines = content.split('\n');
    let processedContent = '';
    for (let line of lines) {
      const trimmedLine = line.trim();
      // Replace ■ (BLACK SQUARE) with HTML checkbox
      if (trimmedLine.includes('■')) {
        const replaced = trimmedLine.replace(/■/g, '<span class="checkbox"></span>');
        processedContent += replaced + '\n';
      }
      // Replace [ ] markers with HTML checkbox
      else if (trimmedLine.match(/^\[\s*\]/)) {
        const textAfterCheckbox = trimmedLine.replace(/^\[\s*\]\s*/, '');
        processedContent += '<span class="checkbox"></span>' + textAfterCheckbox + '\n';
      } else {
        processedContent += line + '\n';
      }
    }
    
    printWindow.document.write('<pre style="white-space:pre-wrap;font-family:Arial;">' + processedContent + '</pre>');
    
    // Footer with PractCom and copyright
    printWindow.document.write('<div class="footer">');
    printWindow.document.write('<div class="footer-title">PractCom</div>');
    printWindow.document.write('<div>© ' + new Date().getFullYear() + ' The Oncall Bot LLC. All rights reserved.</div>');
    printWindow.document.write('<div>Powered by PractCom</div>');
    printWindow.document.write('</div>');
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    // Chrome no longer reliably fires onload from a document.write'd script.
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires; guard prevents the ghost-window flash.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
  };

  // SMS Functions
  const handleTextForm = (form) => {
    setSmsForm(form);
    setSmsPatient(null);
    setSmsPatientSearchQuery('');
    setShowSmsModal(true);
  };

  const sendSMS = async () => {
    if (!smsPatient) {
      toast({
        title: "Patient Required",
        description: "Please select a patient to send the text",
        variant: "destructive",
      });
      return;
    }

    const phoneNumber = smsPatient.cellPhone || smsPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "Selected patient does not have a phone number",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingSMS(true);
      const token = localStorage.getItem('dentalToken');
      const patientName = `${smsPatient.firstName} ${smsPatient.lastName}`;
      
      const response = await fetch(`${backendUrl}/api/sms/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: smsPatient.id,
          document_type: 'management-form',
          document_id: smsForm.id,
          phone_number: phoneNumber
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send SMS');
      }

      toast({
        title: "Text Sent!",
        description: `Form "${smsForm.title}" texted to ${patientName}`,
      });

      setShowSmsModal(false);
      setSmsForm(null);
      setSmsPatient(null);
      setSmsPatientSearchQuery('');
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  // If a form is selected, show the detail page
  if (selectedFormId) {
    return (
      <ManagementFormDetailPage 
        formId={selectedFormId}
        onBack={() => setSelectedFormId(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Management Forms Library</h1>
              <p className="text-gray-600 mt-1">View, edit, download and print office forms</p>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="mb-6">
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Important:</strong> These are template documents. You must customize them for your practice and have them reviewed by your legal counsel before use.
            </AlertDescription>
          </Alert>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search forms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Forms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredForms.map(form => (
            <Card key={form.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{form.title}</CardTitle>
                    <Badge variant="secondary" className="mt-2">
                      {form.category}
                    </Badge>
                  </div>
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewForm(form.id)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewForm(form.id)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTextForm(form)}
                    className="flex items-center justify-center gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Text
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePrintForm(form)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadForm(form)}
                    className="flex items-center justify-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredForms.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No forms found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory !== 'All' 
                ? 'Try adjusting your search or filters'
                : 'No management forms available yet'
              }
            </p>
          </div>
        )}
      </div>

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Text Form to Patient
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to text <span className="font-medium text-blue-600">{smsForm?.title}</span>
            </p>
            
            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={smsPatientSearchQuery}
                onChange={(e) => setSmsPatientSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Patient List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredSmsPatients.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {smsPatientSearchQuery 
                      ? 'No patients with mobile numbers match your search' 
                      : 'No patients with mobile numbers available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with phone numbers can receive text messages</p>
                </div>
              ) : (
                filteredSmsPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSmsPatient(patient)}
                    className={`p-3 border rounded-lg transition cursor-pointer ${
                      smsPatient?.id === patient.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {patient.cellPhone || patient.phone}
                        </p>
                      </div>
                      {smsPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsForm(null);
                  setSmsPatient(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendSMS}
                disabled={!smsPatient || sendingSMS}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {sendingSMS ? 'Sending...' : 'Send Text'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementFormsLibraryPage;
